import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { MatSnackBar } from '@angular/material';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Direccion } from '../../domain/direccion';
import { AddressService } from '../../service/address.service';
import { GenericResponse } from '../../domain/genericResponse';
import { Router } from '@angular/router';
import { GLOBAL } from '../../global';
import { ProvinceService } from '../../service/province.service';
import { Provincia } from '../../domain/provincia';
import { CityService } from '../../service/city.service';
import { Ciudad } from '../../domain/ciudad';
import { UserSession } from '../../domain/user-session';
import { AuthService } from '../../service/auth/auth.service';

@Component({
  selector: 'app-addresses',
  templateUrl: './addresses.component.html',
  styleUrls: ['./addresses.component.scss']
})
export class AddressesComponent implements OnInit, OnDestroy {

  private subscription: Subscription;
  public addressForm: FormGroup;
  public listClientAddresses: Direccion[];
  @Output() onSelectDireccion = new EventEmitter<Direccion>();
  private direccion: Direccion;
  public listAddressAbbreviations: any[];
  public listProvinces: Provincia[];
  public listCities: Ciudad[];
  private direId: number;
  private userSession: UserSession;

  constructor(
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    private addressService: AddressService,
    private router: Router,
    private provinceService: ProvinceService,
    private cityService: CityService,
    private authService: AuthService
  ) {
    this.userSession = this.authService.getUser();
    this.onClear();
  }

  ngOnInit(): void {
    this.addressForm = this.formBuilder.group({
      'clientAddresses': [null],
      'addressName': ['', [Validators.required, Validators.minLength(3), Validators.maxLength(30)]],
      'addressAbbreviations': ['', Validators.required],
      'addressPart1': ['', [Validators.required, Validators.minLength(1), Validators.maxLength(10)]],
      'addressPart2': ['', [Validators.required, Validators.minLength(1), Validators.maxLength(10)]],
      'addressPart3': ['', [Validators.minLength(1), Validators.maxLength(10)]],
      'finalAddress': ['', [Validators.required, Validators.minLength(10), Validators.maxLength(255)]],
      'provinces': ['', Validators.required],
      'cities': ['', Validators.required],
      'observation': ['', [Validators.minLength(3), Validators.maxLength(255)]]
    });
  }

  /**
    * Limpia el formulario.
    * 
    * @author Frank Edward Daza González
    * @version 2018-10-30
    */
  onClear() {
    if(this.addressForm){
      this.addressForm.reset();
    }

    this.direccion = new Direccion();
    this.direccion.tercId = this.userSession.jti;
    this.findAllDireccionesActivasByTercId(this.direccion);
    this.findAllAddressAbbreviations();
    this.getProvinces();
    this.direId = null;
  }

  /**
   * @author Frank Edward Daza González
   * @version 2018-10-29
   */
  ngOnDestroy() {
    if (this.subscription !== null && this.subscription !== undefined)
      this.subscription.unsubscribe();
  }

  /**
    * Retorna todas las direcciones activas asociadas a un cliente.
    * 
    * @author Frank Edward Daza González
    * @version 2018-10-29  
    * @param direccion 
    */
  findAllDireccionesActivasByTercId(direccion: Direccion) {
    this.subscription = this.addressService.findAllDireccionesActivasByTercId(direccion)
      .subscribe((data: GenericResponse) => {
        if (data.codigo === GLOBAL.HTTP_OK) {
          this.listClientAddresses = data.lista;
        }

        if (data.codigo === GLOBAL.HTTP_BAD_REQUEST) {
          this.snackBar.open(data.mensaje, '×', { panelClass: 'error', verticalPosition: 'top', duration: 8000 });
        }
      }, error => {
        this.snackBar.open('¡Ha ocurrido un error al intentar traer las direcciones del cliente, por favor, intenta más tarde!', '×', { panelClass: 'error', verticalPosition: 'top', duration: 8000 });
        this.router.navigate(['/']);
      });
  }

  /**
   * Retorna una lista de tipos de direcciones, 
   * para hidratar el select de addressAbbreviations.
   * 
   * @author Frank Edward Daza González
   * @version 2018-10-30
   */
  findAllAddressAbbreviations() {
    this.listAddressAbbreviations = GLOBAL.TIPOS_DIRECCIONES;
  }

  /**
   * Retorna las provincias del servicio REST de SIGAMOS.
   * 
   * @author Frank Edward Daza González
   * @version 2018-10-30
   */
  getProvinces() {
    this.subscription = this.provinceService.getProvinces()
      .subscribe((data: GenericResponse) => {
        if (data.codigo === GLOBAL.HTTP_OK) {
          this.listProvinces = data.lista;
        }

        if (data.codigo === GLOBAL.HTTP_BAD_REQUEST) {
          this.snackBar.open(data.mensaje, '×', { panelClass: 'error', verticalPosition: 'top', duration: 8000 });
        }
      }, error => {
        this.snackBar.open('¡Ha ocurrido un error al intentar traer las provincias, por favor, intenta más tarde!', '×', { panelClass: 'error', verticalPosition: 'top', duration: 8000 });
        this.router.navigate(['/']);
      });
  }

  /**
   * Obtiene las ciudades asociadas a la provincia seleccionada.
   * 
   * @author Frank Edward Daza González
   * @version 2018-10-30
   */
  onClickProvince() {
    const provId = this.addressForm.controls.provinces.value;

    if (provId !== null && provId !== undefined && provId !== '') {
      const tmpCity = new Ciudad();
      tmpCity.provId = provId;
      this.subscription = this.cityService.getCitiesByProvId(tmpCity)
        .subscribe((data: GenericResponse) => {
          if (data.codigo === GLOBAL.HTTP_OK) {
            this.listCities = data.lista;
          }

          if (data.codigo === GLOBAL.HTTP_BAD_REQUEST) {
            this.snackBar.open(data.mensaje, '×', { panelClass: 'error', verticalPosition: 'top', duration: 8000 });
          }
        }, error => {
          this.snackBar.open('¡Ha ocurrido un error al intentar traer las ciudades, por favor, intenta más tarde!', '×', { panelClass: 'error', verticalPosition: 'top', duration: 8000 });
          this.router.navigate(['/']);
        });
    }
  }

  /**
   * Crea una direccón final.
   * 
   * @author Frank Edward Daza González
   * @version 2018-10-30
   */
  onClickFinalAddress() {
    const addressAbbreviationsTmp = this.addressForm.controls.addressAbbreviations.value.trim();
    const addressPart1Tmp = this.addressForm.controls.addressPart1.value.trim().toUpperCase();
    const addressPart2Tmp = this.addressForm.controls.addressPart2.value.trim().toUpperCase();
    const addressPart3Tmp = this.addressForm.controls.addressPart3.value.trim().toUpperCase();
    this.addressForm.controls.finalAddress.setValue(
      `${addressAbbreviationsTmp} ${addressPart1Tmp} # ${addressPart2Tmp} - ${addressPart3Tmp}`
    );
  }

  /**
   * Crea una direccón asociada a un cliente.
   * 
   * @author Frank Edward Daza González
   * @version 2018-10-30
   */
  onCreateAddress() {
    if (this.addressForm.valid) {
      const addressFinal = new Direccion();
      addressFinal.provId = this.addressForm.controls.provinces.value;
      addressFinal.ciudId = this.addressForm.controls.cities.value;
      addressFinal.direccion = this.addressForm.controls.finalAddress.value.trim();
      addressFinal.esPrincipal = GLOBAL.NO;
      addressFinal.estado = GLOBAL.ESTADO_ACTIVO;
      addressFinal.nombre = this.addressForm.controls.addressName.value.trim();
      addressFinal.observacion = this.addressForm.controls.observation.value.trim();
      addressFinal.usuarioCreador = this.userSession.iss;
      addressFinal.tercId = this.userSession.jti;

      this.subscription = this.addressService.saveClientAddress(addressFinal)
        .subscribe((data: GenericResponse) => {
          if (data.codigo === GLOBAL.HTTP_OK) {
            this.snackBar.open('¡La dirección ha sido creada exitosamente!', '×', { panelClass: 'success', verticalPosition: 'top', duration: 8000 });
            this.onClear();
            //console.log(addressFinal);
            this.onSelectDireccion.emit(addressFinal);
          }

          if (data.codigo === GLOBAL.HTTP_BAD_REQUEST) {
            this.snackBar.open(data.mensaje, '×', { panelClass: 'error', verticalPosition: 'top', duration: 8000 });
          }
        }, error => {
          this.snackBar.open('¡Ha ocurrido un error al intentar crear la dirección, por favor, intenta más tarde!', '×', { panelClass: 'error', verticalPosition: 'top', duration: 8000 });
          this.router.navigate(['/']);
        });
    }
  }

  /**
   * Hidrato el formulario de creación de dirección, con la dirección seleccionada,
   * para modificarla o eliminarla.
   * 
   * @author Frank Edward Daza González
   * @version 2018-10-30
   */
  onChangeClientAddresses() {
    const addressId: number = +this.addressForm.controls.clientAddresses.value;
    let addressTmp: Direccion;

    this.listClientAddresses.forEach(e => {
      if (e.direId === addressId) {
        addressTmp = e;
      }
    });

    this.direId = addressTmp.direId;
    this.addressForm.controls.addressName.setValue(addressTmp.nombre);
    this.addressForm.controls.addressAbbreviations.setValue(' ');
    this.addressForm.controls.addressPart1.setValue(' ');
    this.addressForm.controls.addressPart2.setValue(' ');
    this.addressForm.controls.addressPart3.setValue(' ');
    this.addressForm.controls.finalAddress.setValue(addressTmp.direccion);
    this.addressForm.controls.provinces.setValue(addressTmp.provId);
    this.onClickProvince();
    this.addressForm.controls.cities.setValue(addressTmp.ciudId);
    this.addressForm.controls.observation.setValue(addressTmp.observacion);

    //console.log(addressTmp);
    this.onSelectDireccion.emit(addressTmp);
    //console.log(addressTmp);
  }

  /**
   * Actualiza la dirección asociada a un cliente.
   * 
   * @author Frank Edward Daza González
   * @version 2018-10-31
   */
  onUpdateAddress() {
    if (this.addressForm.valid) {
      const addressFinal = new Direccion();
      addressFinal.direId = this.direId;
      addressFinal.provId = this.addressForm.controls.provinces.value;
      addressFinal.ciudId = this.addressForm.controls.cities.value;
      addressFinal.direccion = this.addressForm.controls.finalAddress.value.trim();
      addressFinal.esPrincipal = GLOBAL.NO;
      addressFinal.estado = GLOBAL.ESTADO_ACTIVO;
      addressFinal.nombre = this.addressForm.controls.addressName.value.trim();
      addressFinal.observacion = this.addressForm.controls.observation.value.trim();
      addressFinal.usuarioModificador = this.userSession.iss;
      addressFinal.tercId = this.userSession.jti;

      this.subscription = this.addressService.updateClientAddress(addressFinal)
        .subscribe((data: GenericResponse) => {
          if (data.codigo === GLOBAL.HTTP_OK) {
            this.snackBar.open('¡La dirección ha sido modificada exitosamente!', '×', { panelClass: 'success', verticalPosition: 'top', duration: 8000 });
            this.onClear();
          }

          if (data.codigo === GLOBAL.HTTP_BAD_REQUEST) {
            this.snackBar.open(data.mensaje, '×', { panelClass: 'error', verticalPosition: 'top', duration: 8000 });
          }
        }, error => {
          this.snackBar.open('¡Ha ocurrido un error al intentar modificar la dirección, por favor, intenta más tarde!', '×', { panelClass: 'error', verticalPosition: 'top', duration: 8000 });
          this.router.navigate(['/']);
      });
    }
  }

  /**
   * Elimina una dirección asociada a un cliente.
   * 
   * @author Frank Edward Daza González
   * @version 2018-10-31
   */
  onClickDeleteAddress() {
    //console.log('Delete button');

    if (confirm("¿Está seguro que desea eliminar la dirección seleccionada?")) {
      if (this.addressForm.controls.clientAddresses.value !== ''
        && this.addressForm.controls.clientAddresses.value !== null
        && this.addressForm.controls.clientAddresses.value !== undefined) {
        
        const addressFinal = new Direccion();
        addressFinal.direId = this.direId;
        addressFinal.usuarioModificador = this.userSession.iss;
        addressFinal.estado = GLOBAL.ESTADO_INACTIVO;

        this.subscription = this.addressService.updateClientAddress(addressFinal)
        .subscribe((data: GenericResponse) => {
          if (data.codigo === GLOBAL.HTTP_OK) {
            this.snackBar.open('¡La dirección ha sido eliminada exitosamente!', '×', { panelClass: 'success', verticalPosition: 'top', duration: 8000 });
            this.onClear();
          }

          if (data.codigo === GLOBAL.HTTP_BAD_REQUEST) {
            this.snackBar.open(data.mensaje, '×', { panelClass: 'error', verticalPosition: 'top', duration: 8000 });
          }
        }, error => {
          this.snackBar.open('¡Ha ocurrido un error al intentar eliminar la dirección, por favor, intenta más tarde!', '×', { panelClass: 'error', verticalPosition: 'top', duration: 8000 });
          this.router.navigate(['/']);
        });
      }
    }    
  }

}
